import React, { useState } from "react";
import { Outlet } from "react-router-dom";

import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useDashboardItems } from "../components/sidebar/dashboardItems";
import Sidebar from "../components/sidebar/Sidebar";
import TopBar from "../components/shell/TopBar";

const drawerWidth = 212;
const collapsedDrawerWidth = 64;
const navId = "app-navigation";

interface DashboardType {
  children?: React.ReactNode;
}

/**
 * Signed-in shell: top bar, then the sidebar (docked from md up, a slide-over drawer
 * below md) beside the page content.
 */
const Dashboard: React.FC<DashboardType> = ({ children }) => {
  const dashboardItems = useDashboardItems();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const handleMenuClick = () => {
    if (isMdUp) {
      setCollapsed((value) => !value);
    } else {
      setMobileOpen((value) => !value);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
      }}
    >
      <TopBar
        onMenuClick={handleMenuClick}
        navExpanded={isMdUp ? !collapsed : mobileOpen}
        navId={navId}
      />
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {isMdUp ? (
          <Sidebar
            variant="permanent"
            items={dashboardItems}
            collapsed={collapsed}
            width={collapsed ? collapsedDrawerWidth : drawerWidth}
            navId={navId}
          />
        ) : (
          <Sidebar
            variant="temporary"
            items={dashboardItems}
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            width={drawerWidth}
            navId={navId}
          />
        )}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            bgcolor: "background.default",
            px: { xs: 4, md: "26px" },
            pt: { xs: 4, md: "22px" },
            pb: { xs: 6, md: "30px" },
          }}
        >
          {children}
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
